import { createI18n } from 'vue-i18n'
import ru from './locales/ru.json'
import en from './locales/en.json'

const SUPPORTED_LOCALES = ['ru', 'en']

// Shared with persistLocale() below, and read by detectLocale() at boot — the localStorage
// key a signed-in user's chosen interface language is remembered under, so a returning
// visitor's next launch starts in their language before auth.fetchMe() has even resolved
// (session auth is cookie-based, not stored client-side, so there is nothing synchronous
// to read from the user object itself at this point in bootstrap).
export const LOCALE_STORAGE_KEY = 'my-goals:locale'

// vue-i18n's built-in pluralisation is the English two-form rule (one | other), which
// renders "1 целей" -- every count taking the same form as five. Russian needs three:
//   one   1, 21, 31 ... but not 11
//   few   2-4, 22-24 ... but not 12-14
//   many  0, 5-20, 25-30 ...
// Message strings for `ru` therefore carry three branches separated by `|`, in that order.
// A message may also carry a dedicated zero branch FIRST ("нет целей | {count} цель |
// ..."), which is the convention vue-i18n's own English rule uses when given three
// branches instead of two. Without honouring it here, a four-branch Russian message would
// render its zero text for every count of one -- the shift is silent, and it looks exactly
// like a mistranslation rather than a rule mismatch.
export function russianPluralIndex(choice, choicesLength = 3) {
  const hasZeroBranch = choicesLength >= 4
  const n = Math.abs(choice)
  if (hasZeroBranch && n === 0) return 0
  const offset = hasZeroBranch ? 1 : 0
  if (n % 10 === 1 && n % 100 !== 11) return offset
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return offset + 1
  return offset + 2
}

const datetimeFormats = {
  ru: { weekday: { weekday: 'long', day: 'numeric', month: 'long' } },
  en: { weekday: { weekday: 'long', day: 'numeric', month: 'long' } },
}

function readStoredLocale() {
  try {
    return globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? null
  } catch {
    // Storage can throw (disabled/private browsing) — treat exactly like "nothing stored".
    return null
  }
}

// Seeds the boot locale: a previously remembered choice wins, otherwise the browser's own
// language, otherwise Russian. Never trusts an unrecognised value from either source —
// only 'ru'/'en' can reach createI18n's `locale` option, which the vue-i18n instance
// requires an entry in `messages` for.
export function detectLocale() {
  const stored = readStoredLocale()
  if (SUPPORTED_LOCALES.includes(stored)) return stored

  const language = globalThis.navigator?.language ?? 'ru'
  return language.toLowerCase().startsWith('en') ? 'en' : 'ru'
}

export function persistLocale(locale) {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Best-effort only: worst case, the next boot falls back to navigator.language again.
  }
}

// Single source of truth for the app's i18n instance. Both the production bootstrap
// (main.js) and the test harness (tests/setup.js) must call this factory rather than
// each constructing their own `createI18n(...)` — otherwise they can silently drift
// apart (different locale, different fallback, different message sets), and a gap
// like "production never installs i18n at all" can hide behind a green test suite
// indefinitely, because the tests never exercised the real bootstrap path.
export function createAppI18n() {
  return createI18n({
    legacy: false,
    locale: detectLocale(),
    fallbackLocale: 'en',
    messages: { ru, en },
    datetimeFormats,
    pluralRules: { ru: russianPluralIndex },
  })
}
